import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Box, Typography, Button, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useParseCsv } from '../hooks';
import { useCsvUpload } from '../providers';

export const SelectCsvStep = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { file, setFile, setCsvParseResults } = useCsvUpload();

  const { result, error, isError, isValid, isLoading } = useParseCsv(file);

  // Update form with parsed results when they change
  useEffect(() => {
    if (result) {
      setCsvParseResults(result);
    }
    if (error) {
      setCsvParseResults(null);
    }
  }, [result, error, setCsvParseResults]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file?.type === 'text/csv') {
      setFile(file);
    }
  };

  const handleClearFile = () => {
    setFile(undefined);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Resets the input field
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('csvImport.steps.select.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('csvImport.steps.select.description')}
      </Typography>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          border: '2px dashed',
          borderColor: 'grey.300',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          mb: 2,
        }}
      >
        {!file && (
          <>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} sx={{ mb: 2 }}>
                {t('csvImport.steps.select.uploadButton')}
              </Button>
            </label>
          </>
        )}

        {file && (
          <Box
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: 1,
              mx: 'auto',
            }}
          >
            <Box
              display="flex"
              gap={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  CSV
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
                {isLoading && (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: 'primary.main',
                    }}
                  />
                )}
                {isValid && !isLoading && (
                  <CheckCircleIcon
                    sx={{
                      color: 'success.main',
                      fontSize: 20,
                    }}
                  />
                )}
                {isError && !isLoading && (
                  <Tooltip title={error?.message ?? t('csvImport.parseErrors')} arrow>
                    <ErrorIcon
                      sx={{
                        color: 'error.main',
                        fontSize: 20,
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={handleClearFile}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
                aria-label={t('csvImport.steps.select.clearFile')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {isError && (
              <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                {t('csvImport.parseErrors')}: {error?.message}
              </Typography>
            )}
            {isValid && result && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                {t('csvImport.parseSuccess', {
                  count: result.data.length - 1, // Assuming there is a header row
                })}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('csvImport.steps.select.requirements')}
      </Typography>
    </Box>
  );
};
